import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { GeminiLiveService, SessionState, SessionSummary } from '../_services/gemini-live.service';

@Component({
  selector: 'app-live-help-widget',
  templateUrl: './live-help-widget.component.html',
  styleUrls: ['./live-help-widget.component.scss']
})
export class LiveHelpWidgetComponent implements OnInit, OnDestroy {

  state: SessionState = 'idle';
  elapsed = '00:00';
  summary: SessionSummary | null = null;
  errorMsg = '';
  isMuted = false;
  showConfirm = false;
  videoEnabled = false;
  videoSecondsLeft = 0;
  sendingSnapshot = false;
  snapshotSent = false;

  private destroy$ = new Subject<void>();

  constructor(private geminiLive: GeminiLiveService) {}

  ngOnInit(): void {
    this.geminiLive.sessionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.state = s;
      });

    this.geminiLive.elapsed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(e => this.elapsed = e);

    this.geminiLive.summary$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => this.summary = s);

    this.geminiLive.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe(err => this.errorMsg = err);

    this.geminiLive.videoEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => this.videoEnabled = v);

    this.geminiLive.videoSecondsLeft$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => this.videoSecondsLeft = s);

    this.geminiLive.micMuted$
      .pipe(takeUntil(this.destroy$))
      .subscribe(m => this.isMuted = m);

    this.geminiLive.sendingSnapshot$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => this.sendingSnapshot = s);

    this.geminiLive.snapshotSent$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => this.snapshotSent = s);
  }

  sendSnapshot(): void {
    this.geminiLive.sendSnapshot();
  }

  get videoCountdownWarning(): boolean {
    return this.videoSecondsLeft > 0 && this.videoSecondsLeft <= 15;
  }

  toggleVideo(): void {
    if (this.videoEnabled) {
      this.geminiLive.disableVideo();
    } else {
      this.geminiLive.enableVideo();
    }
  }

  extendVideo(): void {
    this.geminiLive.extendVideo();
  }

  get videoCountdownText(): string {
    const s = this.videoSecondsLeft;
    const m = Math.floor(s / 60).toString();
    const r = (s % 60).toString().padStart(2, '0');
    return `${m}:${r}`;
  }

  openConfirm(): void {
    this.showConfirm = true;
  }

  cancelConfirm(): void {
    this.showConfirm = false;
  }

  startSession(): void {
    this.showConfirm = false;
    this.geminiLive.startSession();
  }

  endSession(): void {
    this.geminiLive.endSession();
  }

  dismissError(): void {
    this.errorMsg = '';
  }

  dismiss(): void {
    this.geminiLive.dismissSummary();
  }

  toggleMute(): void {
    this.geminiLive.setMuted(!this.isMuted);
  }

  formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
